import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../company-management/constants/permissions';
import './styles/QuickActionsColumn.css';
import { useAppEmailVerificationRestricted } from '../email-verification/EmailVerificationRestrictionContext';
import '../email-verification/appEmailVerificationLock.css';
import {
  CalendarPlusIcon,
  UsersIcon,
  UserPlusIcon,
  CalendarIcon,
  ListIcon,
  TagIcon,
  SendIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from './icons/quick-actions/QuickActionIcons';

const QuickActionsColumn = ({ setIsAddModalOpen }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const navigationLocked = useAppEmailVerificationRestricted();
  const [currentPage, setCurrentPage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const allActions = [
    {
      icon: <CalendarPlusIcon />,
      title: 'Add Appointment',
      onClick: () => navigate('book'),
    },
    {
      icon: <UsersIcon />,
      title: 'Add Meeting',
      onClick: () => navigate('meetings/new'),
    },
    {
      icon: <UserPlusIcon />,
      title: 'Add Client',
      onClick: () => navigate('/app/overview/clients'),
    },
    {
      icon: <CalendarIcon />,
      title: 'View Calendar',
      onClick: () => navigate('/app/overview/calendar'),
    },
    {
      icon: <ListIcon />,
      title: 'View Meetings',
      onClick: () => navigate('/app/overview/meetings/list'),
    },
    ...(hasPermission(PERMISSIONS.SETTINGS.EDIT) ? [{
      icon: <TagIcon />,
      title: 'Manage Services',
      onClick: () =>
        navigate('/app/overview/settings', {
          state: { section: 'services-categories', parent: 'services-categories', servicesTab: 'services' },
        }),
    }] : []),
    ...(hasPermission(PERMISSIONS.SETTINGS.EDIT) ? [{
      icon: <SendIcon />,
      title: 'Send Reminders',
      onClick: () =>
        navigate('/app/overview/settings', {
          state: { section: 'notifications', parent: 'notifications', messagingTab: 'email' },
        }),
    }] : []),
  ].sort((a, b) => a.title.localeCompare(b.title));

  // Show only first 10 items
  const displayActions = allActions.slice(0, 10);

  // Mobile pagination logic
  const itemsPerPage = isMobile ? 5 : 10;
  const totalPages = isMobile ? Math.ceil(displayActions.length / itemsPerPage) : 1;
  const currentActions = isMobile 
    ? displayActions.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
    : displayActions;

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextClick = (e) => {
    nextPage();
    e?.currentTarget?.blur();
  };

  const handlePrevClick = (e) => {
    prevPage();
    e?.currentTarget?.blur();
  };

  // Reset to first page when switching between mobile/desktop
  useEffect(() => {
    setCurrentPage(0);
  }, [isMobile]);

  return (
    <>
      <div className={`quick-actions-container${navigationLocked ? ' quick-actions-container--locked' : ''}`}>
        <div className="quick-actions-grid">
          {currentActions.map((action, index) => (
            <button
              key={index}
              type="button"
              className="quick-action-button"
              onClick={action.onClick}
            >
              <span className="quick-action-icon">{action.icon}</span>
              <span className="quick-action-label">{action.title}</span>
            </button>
          ))}
        </div>
        
        {isMobile && totalPages > 1 && (
          <div className="pagination-controls">
            <button
              type="button"
              className="pagination-button pagination-prev"
              onClick={handlePrevClick}
              disabled={currentPage === 0}
              aria-label="Previous page"
            >
              <ChevronLeftIcon />
            </button>
            <span className="page-indicator" aria-live="polite">
              <span className="page-indicator-inner">{currentPage + 1} / {totalPages}</span>
            </span>
            <button
              type="button"
              className="pagination-button pagination-next"
              onClick={handleNextClick}
              disabled={currentPage === totalPages - 1}
              aria-label="Next page"
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </div>

      
    </>
  );
};

export default QuickActionsColumn; 